// @flow
import * as React from 'react';

import { selectCanChooseResellee } from '@gandi/cart';
import { selectCurrentId } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';

import { useCartSummaryContext } from '../../../Layout/context/CartSummary';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { selectShopCurrent } from '../../../modules/organization/selectors';
import type {
  ComponentWithPrefetch,
  FullState,
  PreFetchArgs,
} from '../../../types';

import { selectCanChooseProductOwner } from '../modules/selectors';

import { Container } from './Container';
import CustomerSelect from './CustomerSelect';
import OwnerSelect from './OwnerSelect';
import ResellerOwnerType, {
  CUSTOMER,
  RESELLER,
  ORGANIZATION,
  type OwnerType,
} from './ResellerOwnerType';
import ResellerSelfSelect from './ResellerSelfSelect';

export type Props = {
  chooseOwner: boolean,
  chooseResellee: boolean,
  setOwnerType: (OwnerType | null) => void,
};

export type ChildPreFetchProps = {
  chooseOwner: Props['chooseOwner'],
  chooseResellee: Props['chooseResellee'],
};

function OwnersBlock({
  chooseOwner,
  chooseResellee,
  setOwnerType,
}: Props): React.Node {
  const { __ } = useTranslator();

  const currentId = useAppSelector(selectCurrentId);
  const { frozen, ownerType } = useCartSummaryContext();

  return (
    <Container title={__('Product owner:')}>
      {chooseResellee && chooseOwner && ownerType && (
        <ResellerOwnerType type={ownerType} onChange={setOwnerType} />
      )}
      {chooseResellee && ownerType === CUSTOMER && (
        <CustomerSelect frozen={frozen} />
      )}
      {chooseResellee && ownerType === RESELLER && <ResellerSelfSelect />}
      {chooseOwner && ownerType === ORGANIZATION && (
        <OwnerSelect frozen={frozen} key={currentId} />
      )}
    </Container>
  );
}

OwnersBlock.preFetch = (
  args: PreFetchArgs<{}, any, any, FullState>,
): Promise<any> => {
  const { getState } = args;
  const org = selectShopCurrent(getState(), true);

  const chooseResellee = org.reseller && selectCanChooseResellee(getState());
  const chooseOwner = selectCanChooseProductOwner(getState());

  // TODO: Check why we have twice OwnerSelect.preFetch
  return Promise.allSettled([
    chooseOwner && OwnerSelect.preFetch(args),
    chooseResellee && CustomerSelect.preFetch(args),
  ]);
};

type OwnersBlockType = ComponentWithPrefetch<
  Props,
  typeof OwnersBlock.preFetch,
>;

export default (OwnersBlock: OwnersBlockType);
